import Header from '@components/common/layout/Header';
import MigrationJsonEditor from './MigrationJsonEditor';
import MigrationTextView from './MigrationTextView';
import MigrationTreeView from './MigrationTreeView';
import OrgAutocomplete from './OrgAutocomplete';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import ValidationWarnings from './ValidationWarnings';
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Check,
  Download,
  ExpandLess,
  ExpandMore,
  FileDownload,
  Image,
  MoreHoriz,
  PlayArrow,
  Refresh,
  Save,
  Upload,
} from '@mui/icons-material';
import {
  checkMigrationOrgs,
  executeMigration,
  getMigrationData,
  loadMigrationPlans,
  modifyMigrationPlan,
  saveMigrationPlan,
  validateMigration,
} from '@account/api/migration';
import {getOrganization} from '@account/api/organizations';
import {toPng, toSvg} from 'html-to-image';
import {useHistory, useLocation} from 'react-router-dom';
import {useMutation, useQuery} from '@tanstack/react-query';

const MigrationWizard = () => {
  const location = useLocation();
  const history = useHistory();
  const [selectedOrgIds, setSelectedOrgIds] = useState([]);
  const [appliedOrgIds, setAppliedOrgIds] = useState([]);
  const [showJson, setShowJson] = useState(false);
  const [unrelatedOrgs, setUnrelatedOrgs] = useState([]);
  const [showOrgWarning, setShowOrgWarning] = useState(false);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [discoveredOrgsCount, setDiscoveredOrgsCount] = useState(0);
  const [isLoadingOrgFromUrl, setIsLoadingOrgFromUrl] = useState(false);
  const [viewMode, setViewMode] = useState('diagram');
  const [orgSectionExpanded, setOrgSectionExpanded] = useState(true);
  const [importedMigrationData, setImportedMigrationData] = useState(null);
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [operationsMenuAnchor, setOperationsMenuAnchor] = useState(null);
  const [showExecuteDialog, setShowExecuteDialog] = useState(false);

  // Track the current plan ID for link modifications
  const [currentPlanId, setCurrentPlanId] = useState(null);

  // Plan selection state
  const [selectedPlanType, setSelectedPlanType] = useState('generate'); // 'generate', 'user', 'inferred', 'derived'

  // Track plan modifications state
  const [originalPlanData, setOriginalPlanData] = useState(null);
  const [planModifications, setPlanModifications] = useState({
    linkModifications: [],
    usageLinkModifications: [],
    productAccessModifications: [],
  });
  const [hasModifications, setHasModifications] = useState(false);

  // Parse URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orgIdParam = params.get('org-id');
    const orgIdsParam = params.get('org-ids'); // Support both single and multiple
    const showJsonParam = params.get('show_json') === 'true';

    if (orgIdsParam) {
      // Multiple org IDs separated by commas
      const initialOrgIds = orgIdsParam.split(',').filter(id => id.trim());

      // Validate that all org IDs are integers
      for (const id of initialOrgIds) {
        if (!Number.isInteger(Number(id)) || Number(id) <= 0) {
          throw new Error(
            `Invalid organization ID: "${id}". Organization IDs must be positive integers.`
          );
        }
      }

      setSelectedOrgIds(initialOrgIds);
    } else if (orgIdParam) {
      // Single org ID for backward compatibility
      if (!Number.isInteger(Number(orgIdParam)) || Number(orgIdParam) <= 0) {
        throw new Error(
          `Invalid organization ID: "${orgIdParam}". Organization ID must be a positive integer.`
        );
      }
      const initialOrgIds = [orgIdParam];
      setSelectedOrgIds(initialOrgIds);
    }

    setShowJson(showJsonParam);
  }, [location.search]);

  // Fetch organization objects when selectedOrgIds change (for URL param support)
  useEffect(() => {
    if (selectedOrgIds.length > 0 && selectedOrgs.length === 0) {
      // Only fetch if we have IDs but no org objects (i.e., from URL param)
      setIsLoadingOrgFromUrl(true);
      Promise.all(
        selectedOrgIds.map(orgId => getOrganization(parseInt(orgId, 10)))
      )
        .then(fetchedOrgs => {
          setSelectedOrgs(fetchedOrgs);
          setIsLoadingOrgFromUrl(false);
        })
        .catch(() => {
          // Silently fail - organization may not exist or user may not have access
          setSelectedOrgIds([]);
          setIsLoadingOrgFromUrl(false);
        });
    }
  }, [selectedOrgIds, selectedOrgs.length]);

  // State for tracking plan generation type
  const [currentPlanType, setCurrentPlanType] = useState('inferred');

  // Focus selection state - both options selected by default
  const focusOptions = [
    {name: 'Tile View Bundles', value: 'tile_view_bundles'},
    {name: 'SkySat Bundles', value: 'skysat_bundles'},
  ];
  const [selectedFocus, setSelectedFocus] = useState([
    'tile_view_bundles',
    'skysat_bundles',
  ]);

  // Fetch migration data
  const {
    data: migrationData,
    isFetching: isMigrationLoading,
    error: migrationError,
    refetch: refetchMigrationData,
  } = useQuery({
    queryKey: ['migration-data', appliedOrgIds, currentPlanType, selectedFocus],
    queryFn: () =>
      getMigrationData(appliedOrgIds, currentPlanType, selectedFocus),
    enabled:
      appliedOrgIds.length > 0 &&
      (currentPlanType === 'base' || currentPlanType === 'inferred'),
    retry: false, // Don't retry on 500 errors
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 0,
    cacheTime: 0,
  });

  // Fetch validation data separately for the Validation tab
  const {
    data: validationData,
    isFetching: isValidationLoading,
    error: validationError,
  } = useQuery({
    queryKey: ['migration-validation', appliedOrgIds],
    queryFn: async () => {
      try {
        return await validateMigration(appliedOrgIds);
      } catch (error) {
        // If it's a 400 error with validation data, extract the data
        if (
          error.response &&
          error.response.status === 400 &&
          error.response.data
        ) {
          return error.response.data;
        }
        // Re-throw other errors
        throw error;
      }
    },
    enabled: appliedOrgIds.length > 0,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 0,
    cacheTime: 0,
  });

  // Lightweight queries to check what plans are available (for button states)
  const {data: availableUserPlans} = useQuery({
    queryKey: ['available-user-plans', selectedOrgIds],
    queryFn: () => loadMigrationPlans(selectedOrgIds, 'user'),
    enabled: selectedOrgIds.length > 0,
    retry: false,
    staleTime: 300000,
    cacheTime: 300000,
  });

  const {data: availableInferredPlans} = useQuery({
    queryKey: ['available-inferred-plans', selectedOrgIds],
    queryFn: () => loadMigrationPlans(selectedOrgIds, 'inferred'),
    enabled: selectedOrgIds.length > 0,
    retry: false,
    staleTime: 300000,
    cacheTime: 300000,
  });

  const {
    data: availableDerivedPlans, // eslint-disable-line no-unused-vars
  } = useQuery({
    queryKey: ['available-derived-plans', selectedOrgIds],
    queryFn: () => loadMigrationPlans(selectedOrgIds, 'derived'),
    enabled: selectedOrgIds.length > 0,
    retry: false,
    staleTime: 300000,
    cacheTime: 300000,
  });

  // Actual plan data queries (only run when user selects a plan type)
  const {
    data: savedUserPlansData,
    isFetching: isUserPlansLoading,
    refetch: refetchUserPlans,
  } = useQuery({
    queryKey: ['saved-migration-plans-user', appliedOrgIds],
    queryFn: () => loadMigrationPlans(appliedOrgIds, 'user'),
    enabled: appliedOrgIds.length > 0 && selectedPlanType === 'user',
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 300000,
    cacheTime: 300000,
  });

  const {
    data: savedInferredPlansData,
    isFetching: isInferredPlansLoading,
    refetch: refetchInferredPlans,
  } = useQuery({
    queryKey: ['saved-migration-plans-inferred', appliedOrgIds],
    queryFn: () => loadMigrationPlans(appliedOrgIds, 'inferred'),
    enabled: appliedOrgIds.length > 0 && selectedPlanType === 'inferred',
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 300000,
    cacheTime: 300000,
  });

  const {
    data: savedDerivedPlansData,
    isFetching: isDerivedPlansLoading, // eslint-disable-line no-unused-vars
    refetch: refetchDerivedPlans,
  } = useQuery({
    queryKey: ['saved-migration-plans-derived', appliedOrgIds],
    queryFn: () => loadMigrationPlans(appliedOrgIds, 'derived'),
    enabled: appliedOrgIds.length > 0 && selectedPlanType === 'derived',
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 300000,
    cacheTime: 300000,
  });

  // Determine which migration data to use based on selected plan type
  const activeMigrationData = useMemo(() => {
    switch (selectedPlanType) {
      case 'user':
        if (
          savedUserPlansData &&
          savedUserPlansData.results &&
          savedUserPlansData.results.length > 0
        ) {
          const mostRecentPlan = savedUserPlansData.results[0];
          // Transform saved plan data to match migration preview format
          return {
            results: {
              status: 'success',
              migration_plan: mostRecentPlan.migration_plan,
            },
            plan_id: mostRecentPlan.plan_id,
            organizations: mostRecentPlan.organization_ids,
            kind: mostRecentPlan.kind,
          };
        }
        break;
      case 'inferred':
        if (
          savedInferredPlansData &&
          savedInferredPlansData.results &&
          savedInferredPlansData.results.length > 0
        ) {
          const mostRecentPlan = savedInferredPlansData.results[0];
          // Transform saved plan data to match migration preview format
          return {
            results: {
              status: 'success',
              migration_plan: mostRecentPlan.migration_plan,
            },
            plan_id: mostRecentPlan.plan_id,
            organizations: mostRecentPlan.organization_ids,
            kind: mostRecentPlan.kind,
          };
        }
        // Fall back to generated migration data for inferred plans
        return migrationData;
      case 'derived':
        if (
          savedDerivedPlansData &&
          savedDerivedPlansData.results &&
          savedDerivedPlansData.results.length > 0
        ) {
          const mostRecentPlan = savedDerivedPlansData.results[0];
          // Transform saved plan data to match migration preview format
          return {
            results: {
              status: 'success',
              migration_plan: mostRecentPlan.migration_plan,
            },
            plan_id: mostRecentPlan.plan_id,
            organizations: mostRecentPlan.organization_ids,
            kind: mostRecentPlan.kind,
          };
        }
        break;
      default:
        // Fall back to generated migration data
        return migrationData;
    }
    // If selected plan type has no data, fall back to generated
    return migrationData;
  }, [
    selectedPlanType,
    savedUserPlansData,
    savedInferredPlansData,
    savedDerivedPlansData,
    migrationData,
  ]);

  // Calculate validation counts for badge display
  const validationCounts = useMemo(() => {
    // Prioritize dedicated validation data, then fall back to existing migration data
    const validation =
      validationData?.results?.validation ||
      (importedMigrationData || activeMigrationData)?.results?.validation;

    if (!validation || !validation.messages) {
      return {errors: 0, warnings: 0, hasIssues: false};
    }

    const errors = validation.messages.filter(msg => {
      const severity = msg.severity?.toLowerCase?.() || '';
      return severity.includes('error');
    }).length;

    const warnings = validation.messages.filter(msg => {
      const severity = msg.severity?.toLowerCase?.() || '';
      return severity.includes('warning');
    }).length;

    return {
      errors,
      warnings,
      hasIssues: errors > 0 || warnings > 0,
    };
  }, [validationData, importedMigrationData, activeMigrationData]);

  // Handler for when a migration plan is modified
  const handlePlanModified = useCallback(
    newPlanId => {
      setCurrentPlanId(newPlanId);
      // Refetch the appropriate plans based on current selection
      if (selectedPlanType === 'user') {
        refetchUserPlans();
      } else if (selectedPlanType === 'inferred') {
        refetchInferredPlans();
      } else if (selectedPlanType === 'derived') {
        refetchDerivedPlans();
      }
    },
    [
      selectedPlanType,
      refetchUserPlans,
      refetchInferredPlans,
      refetchDerivedPlans,
    ]
  );

  // Extract plan ID from active migration data when available
  useEffect(() => {
    if (activeMigrationData?.plan_id) {
      setCurrentPlanId(activeMigrationData.plan_id);
    } else if (importedMigrationData?.plan_id) {
      setCurrentPlanId(importedMigrationData.plan_id);
    } else if (activeMigrationData?.results?.migration_plan) {
      // For newly generated plans without a saved ID, create a temporary ID
      const tempId = `temp-${Date.now()}`;
      setCurrentPlanId(tempId);
    }
  }, [activeMigrationData, importedMigrationData]);

  // Track check success state
  const [checkSuccessful, setCheckSuccessful] = useState(false);

  // Store original plan data when active migration data changes
  useEffect(() => {
    if (activeMigrationData?.results?.migration_plan && !originalPlanData) {
      setOriginalPlanData(activeMigrationData.results.migration_plan);
    }
  }, [activeMigrationData, originalPlanData]);

  // Plan modification handlers
  const addLinkModification = useCallback(
    (subscriptionId, lineItemId, confidence = 1.0) => {
      setPlanModifications(prev => {
        const newMods = prev.linkModifications.filter(
          m => m.subscription_id !== subscriptionId
        );
        newMods.push({
          subscription_id: subscriptionId,
          line_item_id: lineItemId,
          link_confidence: confidence,
        });
        return {...prev, linkModifications: newMods};
      });
      setHasModifications(true);
    },
    []
  );

  const removeUsageLinkModification = useCallback(
    subscriptionId => {
      // Find existing usage links for this subscription from the current migration data
      const activePlan = activeMigrationData?.results?.migration_plan;
      const usageLinks = activePlan?.overlay?.proposed_usage_links || [];
      const targetUsageLink = usageLinks.find(
        link => link.subscription_id === subscriptionId
      );

      if (targetUsageLink && targetUsageLink.product_access_usage) {
        setPlanModifications(prev => {
          // Remove existing modifications for this subscription
          const filteredMods = prev.usageLinkModifications.filter(
            m => m.subscription_id !== subscriptionId
          );

          // Add removal modifications for each product access linked to this subscription
          targetUsageLink.product_access_usage.forEach(pau => {
            filteredMods.push({
              subscription_id: subscriptionId,
              product_access_unique_id: pau.product_access_unique_id,
              use_billable_id: false, // Set to false to remove the link
            });
          });

          return {...prev, usageLinkModifications: filteredMods};
        });
        setHasModifications(true);
      }
    },
    [activeMigrationData]
  );

  const addProductAccessModification = useCallback(
    (uniqueId, modifications) => {
      setPlanModifications(prev => {
        const newMods = prev.productAccessModifications.filter(
          m => m.product_access_unique_id !== uniqueId
        );
        newMods.push({
          product_access_unique_id: uniqueId,
          ...modifications,
        });
        return {...prev, productAccessModifications: newMods};
      });
      setHasModifications(true);
    },
    []
  );

  const resetPlanModifications = useCallback(() => {
    setPlanModifications({
      linkModifications: [],
      usageLinkModifications: [],
      productAccessModifications: [],
    });
    setHasModifications(false);
    // Restore original data
    if (originalPlanData) {
      refetchMigrationData();
    }
  }, [originalPlanData, refetchMigrationData]);

  const bypassCheck = () => {
    setDiscoveredOrgsCount(0);
    setUnrelatedOrgs([]);
    setShowOrgWarning(false);
    // Mark check as successful (don't auto-preview)
    setCheckSuccessful(true);
  };

  /* eslint-disable no-unused-vars */
  const performCheck = async data => {
    // Extract all related organization IDs from the nested structure
    const relatedOrgIds = new Set();
    const relatedOrgs = data.related_organizations || {};

    // Combine all organization IDs from subscription, salesforce, parent orgs, and suborgs
    Object.values(relatedOrgs.subscriptions_reference || {}).forEach(
      orgList => {
        orgList.forEach(orgId => relatedOrgIds.add(orgId));
      }
    );
    Object.values(relatedOrgs.salesforce_orders_pl_number || {}).forEach(
      orgList => {
        orgList.forEach(orgId => relatedOrgIds.add(orgId));
      }
    );

    // Add parent orgs
    (relatedOrgs.parent_orgs || []).forEach(orgId => {
      relatedOrgIds.add(orgId);
    });

    // Add suborgs
    (relatedOrgs.suborgs || []).forEach(orgId => {
      relatedOrgIds.add(orgId);
    });

    // Find missing related orgs (not in current selection)
    const currentOrgIds = new Set(selectedOrgIds.map(id => parseInt(id)));
    const missingRelatedOrgIds = Array.from(relatedOrgIds).filter(
      orgId => !currentOrgIds.has(orgId)
    );

    // Find unrelated orgs to remove
    const unrelatedOrgIds = data.unrelated_organizations || [];
    const unrelatedOrgObjects = selectedOrgs.filter(org =>
      unrelatedOrgIds.includes(org.id)
    );

    // Fetch org details for all missing related orgs
    if (missingRelatedOrgIds.length > 0) {
      try {
        const fetchedOrgs = await Promise.all(
          missingRelatedOrgIds.map(orgId => getOrganization(orgId))
        );

        // Automatically append discovered orgs to selection
        const updatedOrgs = [...selectedOrgs, ...fetchedOrgs];
        setSelectedOrgs(updatedOrgs);
        setSelectedOrgIds(updatedOrgs.map(org => org.id.toString()));
        setDiscoveredOrgsCount(fetchedOrgs.length);
      } catch {
        // Failed to fetch discovered organizations
        setDiscoveredOrgsCount(0);
      }
    } else {
      setDiscoveredOrgsCount(0);
    }

    // Show warning if there are unrelated orgs to remove
    if (unrelatedOrgObjects.length > 0) {
      setUnrelatedOrgs(unrelatedOrgObjects);
      setShowOrgWarning(true);
    } else {
      setUnrelatedOrgs([]);
      setShowOrgWarning(false);
    }

    // Mark check as successful (don't auto-preview)
    setCheckSuccessful(true);
  };
  /* eslint-enable no-unused-vars */

  // Check for missing orgs mutation
  const checkOrgsMutation = useMutation({
    mutationFn: checkMigrationOrgs,
    // TODO: bypassCheck -> performCheck after backend /check endpoint is fixed
    // The check endpoint currently has a bug where it returns cloned orgs
    // for eval orgs due to backend PL number association logic issues
    onSuccess: bypassCheck,
    onError: () => {
      setShowOrgWarning(false);
      setCheckSuccessful(false);
    },
  });

  // Execute migration mutation
  const executeMigrationMutation = useMutation({
    mutationFn: ({organizationIds, mode}) =>
      executeMigration(organizationIds, mode),
    onSuccess: data => {
      setShowExecuteDialog(false);
      if (data.results?.provisioning_status === 'success') {
        alert(
          `Migration executed successfully! ${data.results.line_items_created} line items created.`
        );
      } else {
        alert(
          `Migration execution failed: ${data.results?.error_message || 'Unknown error'}`
        );
      }
    },
    onError: error => {
      setShowExecuteDialog(false);
      alert(`Migration execution failed: ${error.message || 'Unknown error'}`);
    },
  });

  // Plan modification mutation
  const modifyPlanMutation = useMutation({
    mutationFn: ({planId, modifications}) =>
      modifyMigrationPlan(appliedOrgIds, planId, modifications),
    onSuccess: data => {
      handlePlanModified(data.plan_id);
      // Switch to user plan view to show the modified plan
      setSelectedPlanType('user');
      // Reset modifications after successful save
      setPlanModifications({
        linkModifications: [],
        usageLinkModifications: [],
        productAccessModifications: [],
      });
      setHasModifications(false);
    },
    onError: error => {
      alert(
        `Failed to save modifications: ${error.message || 'Unknown error'}`
      );
    },
  });

  // Note: We use modifyPlanMutation for all interactive modifications
  // The saveMigrationPlan API is only used for JSON import/export workflow

  // Save migration plan mutation for JSON editor
  const saveMigrationPlanMutation = useMutation({
    mutationFn: migrationPlan => {
      if (!appliedOrgIds.length) {
        throw new Error('No organizations selected for migration plan');
      }
      return saveMigrationPlan(appliedOrgIds, migrationPlan);
    },
    onSuccess: () => {
      // Refresh the user plans to show the updated plan
      refetchUserPlans();
      setSelectedPlanType('user');
    },
    onError: error => {
      alert(
        `Failed to save migration plan: ${error.message || 'Unknown error'}`
      );
    },
  });

  const savePlanModifications = useCallback(() => {
    if (!currentPlanId || !hasModifications) return;

    // For temporary plan IDs, we need to save the base plan first, then modify
    if (currentPlanId.startsWith('temp-')) {
      // First save the base plan to get a real plan ID
      saveMigrationPlan(
        appliedOrgIds,
        activeMigrationData?.results?.migration_plan
      )
        .then(saveResult => {
          if (saveResult.status === 'success') {
            // Now modify the saved plan
            return modifyMigrationPlan(
              appliedOrgIds,
              saveResult.plan_id,
              planModifications
            );
          } else {
            throw new Error(
              saveResult.error_message || 'Failed to save base plan'
            );
          }
        })
        .then(modifyResult => {
          if (modifyResult.status === 'success') {
            handlePlanModified(modifyResult.plan_id);
            // Switch to user plan view to show the modified plan
            setSelectedPlanType('user');
            // Reset modifications after successful save
            setPlanModifications({
              linkModifications: [],
              usageLinkModifications: [],
              productAccessModifications: [],
            });
            setHasModifications(false);
          } else {
            alert(
              `Failed to modify plan: ${modifyResult.error_message || 'Unknown error'}`
            );
          }
        })
        .catch(error => {
          alert(
            `Failed to save and modify plan: ${error.message || 'Unknown error'}`
          );
        });
    } else {
      // Plan already exists in database, can modify directly
      modifyPlanMutation.mutate({
        planId: currentPlanId,
        modifications: planModifications,
      });
    }
  }, [
    currentPlanId,
    hasModifications,
    planModifications,
    appliedOrgIds,
    activeMigrationData,
    modifyPlanMutation,
    handlePlanModified,
  ]);

  // Function to update URL query parameters
  const updateUrlParams = orgIds => {
    const params = new URLSearchParams(location.search);

    // Clean up existing org-related params
    params.delete('org-id');
    params.delete('org-ids');

    if (orgIds.length === 1) {
      // Single org ID - use org-id for simplicity
      params.set('org-id', orgIds[0]);
    } else if (orgIds.length > 1) {
      // Multiple org IDs - use org-ids with comma separation
      params.set('org-ids', orgIds.join(','));
    }
    // If orgIds.length === 0, both params are deleted (no orgs selected)

    const newUrl = `${location.pathname}?${params.toString()}`;
    history.replace(newUrl);
  };

  const handleOrgSelection = (_event, newValue) => {
    setSelectedOrgs(newValue);
    const newSelectedOrgIds = newValue.map(org => org.id.toString());
    setSelectedOrgIds(newSelectedOrgIds);

    // Update URL query parameters
    updateUrlParams(newSelectedOrgIds);

    // Determine if user is adding new orgs or just removing existing ones
    const currentOrgIdSet = new Set(selectedOrgs.map(org => org.id));
    const hasNewOrgs = newValue.some(org => !currentOrgIdSet.has(org.id));

    // Only reset check state if user is adding new orgs
    // This allows users to remove orgs after Check without disabling Preview
    if (hasNewOrgs) {
      setCheckSuccessful(false);
      setAppliedOrgIds([]); // Clear preview
    }

    // Always reset these when selection changes
    setUnrelatedOrgs([]);
    setShowOrgWarning(false);
    setDiscoveredOrgsCount(0);
  };

  const handleCheck = () => {
    if (selectedOrgIds.length === 0) return;

    // Check for missing related orgs
    checkOrgsMutation.mutate(selectedOrgIds);
  };

  const handlePreviewMigration = (planType = 'inferred') => {
    if (selectedOrgIds.length === 0) return;

    // Set the plan type and clear imported data when generating new plans
    setCurrentPlanType(planType);
    setImportedMigrationData(null);
    setShowSaveButton(false);

    // If already previewing same orgs with same plan type, force refetch
    if (
      appliedOrgIds.length > 0 &&
      appliedOrgIds.sort().join(',') === selectedOrgIds.sort().join(',') &&
      currentPlanType === planType
    ) {
      refetchMigrationData();
    } else {
      // Apply the selected org IDs to trigger migration preview
      setAppliedOrgIds(selectedOrgIds);
    }
  };

  const handleSelectPlanType = planType => {
    if (selectedOrgIds.length === 0) return;

    // Set the selected plan type
    setSelectedPlanType(planType);
    setImportedMigrationData(null);
    setShowSaveButton(false);

    // Apply the selected org IDs to enable queries
    setAppliedOrgIds(selectedOrgIds);

    // Set current plan type for save operations
    setCurrentPlanType(planType === 'generate' ? 'user' : planType);
  };

  const handleAcceptAdjustedList = () => {
    // Remove unrelated orgs from selection
    const adjustedOrgs = selectedOrgs.filter(
      org => !unrelatedOrgs.some(unrelated => unrelated.id === org.id)
    );
    const adjustedOrgIds = adjustedOrgs.map(org => org.id.toString());

    // Update selection
    setSelectedOrgs(adjustedOrgs);
    setSelectedOrgIds(adjustedOrgIds);

    // Apply the adjusted list directly to migration preview
    setAppliedOrgIds(adjustedOrgIds);
    setShowOrgWarning(false);
  };

  const handleExportJSON = () => {
    const dataToExport = importedMigrationData || activeMigrationData;
    if (!dataToExport) return;

    const jsonString = JSON.stringify(dataToExport, null, 2);
    const blob = new Blob([jsonString], {type: 'application/json'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `migration-plan-${appliedOrgIds.join('-')}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleImportJSON = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async event => {
      const file = event.target.files[0];
      if (!file) return;

      try {
        const text = await file.text();
        const jsonData = JSON.parse(text);

        // Validate that it's a valid migration data structure
        if (!jsonData.results?.migration_plan?.context) {
          alert(
            'Invalid migration plan JSON format. Please ensure the file contains a valid migration plan.'
          );
          return;
        }

        // Extract organization IDs from the imported data
        const importedOrgIds =
          jsonData.results.migration_plan.context.org_ids || [];

        // Set the imported data and trigger the save workflow
        setImportedMigrationData(jsonData);
        setShowSaveButton(true);

        // Update the org selection to match imported data
        if (importedOrgIds.length > 0) {
          try {
            const fetchedOrgs = await Promise.all(
              importedOrgIds.map(orgId => getOrganization(orgId))
            );
            setSelectedOrgs(fetchedOrgs);
            setSelectedOrgIds(importedOrgIds.map(id => id.toString()));
            setAppliedOrgIds(importedOrgIds.map(id => id.toString()));
            setCheckSuccessful(true);
          } catch (error) {
            alert(
              'Successfully imported migration plan, but failed to load organization details. Please select organizations manually.'
            );
          }
        }
      } catch (error) {
        alert(
          'Failed to parse JSON file. Please ensure it contains valid JSON.'
        );
      }
    };
    input.click();
  };

  const handleSaveMigrationPlan = async () => {
    if (!importedMigrationData) return;

    try {
      // Extract the migration plan from the imported data
      const migrationPlan = importedMigrationData.results?.migration_plan;
      if (!migrationPlan) {
        alert('Invalid migration plan data. Unable to save.');
        return;
      }

      // Call backend API to save the user-defined migration plan
      const result = await saveMigrationPlan(appliedOrgIds, migrationPlan);

      if (result.status === 'success') {
        alert(`Migration plan saved successfully! Plan ID: ${result.plan_id}`);
        setShowSaveButton(false);
      } else {
        alert(`Failed to save migration plan: ${result.error_message}`);
      }
    } catch (error) {
      alert('Failed to save migration plan. Please try again.');
    }
  };

  const handleExecutePlan = () => {
    setShowExecuteDialog(true);
  };

  const handleConfirmExecution = () => {
    if (!appliedOrgIds.length) return;

    executeMigrationMutation.mutate({
      organizationIds: appliedOrgIds,
    });
  };

  const downloadImage = (dataUrl, filename) => {
    const link = document.createElement('a');
    link.download = filename;
    link.href = dataUrl;
    link.click();
  };

  const handleExportSVG = () => {
    const viewport = document.querySelector('.react-flow__viewport');
    if (!viewport) return;

    toSvg(viewport, {
      backgroundColor: '#ffffff',
      filter: node => {
        // Exclude controls, minimap, and panels from export
        if (node?.classList?.contains('react-flow__controls')) return false;
        if (node?.classList?.contains('react-flow__minimap')) return false;
        if (node?.classList?.contains('react-flow__panel')) return false;
        if (node?.classList?.contains('react-flow__background')) return false;
        return true;
      },
    })
      .then(dataUrl => {
        downloadImage(dataUrl, 'migration-preview.svg');
      })
      .catch(() => {
        // Export failed - silently ignore
      });
  };

  const handleExportPNG = () => {
    const viewport = document.querySelector('.react-flow__viewport');
    if (!viewport) return;

    // High resolution PNG (2x scale)
    toPng(viewport, {
      backgroundColor: '#ffffff',
      pixelRatio: 2, // 2x resolution for high quality
      filter: node => {
        // Exclude controls, minimap, and panels from export
        if (node?.classList?.contains('react-flow__controls')) return false;
        if (node?.classList?.contains('react-flow__minimap')) return false;
        if (node?.classList?.contains('react-flow__panel')) return false;
        if (node?.classList?.contains('react-flow__background')) return false;
        return true;
      },
    })
      .then(dataUrl => {
        downloadImage(dataUrl, 'migration-preview-highres.png');
      })
      .catch(() => {
        // Export failed - silently ignore
      });
  };

  // JSON editor handlers
  const handleJsonSave = useCallback(
    updatedMigrationData => {
      saveMigrationPlanMutation.mutate(updatedMigrationData);
    },
    [saveMigrationPlanMutation]
  );

  const handleJsonDiscard = useCallback(() => {
    // The JSON editor component handles its own state reset
    // No additional action needed here
  }, []);

  if (showJson && activeMigrationData) {
    return (
      <Box sx={{p: 3}}>
        <Typography variant="h5" sx={{mb: 2}}>
          Migration Data (JSON)
        </Typography>
        <Paper sx={{p: 2}}>
          <pre style={{whiteSpace: 'pre-wrap', fontSize: '12px'}}>
            {JSON.stringify(activeMigrationData, null, 2)}
          </pre>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Header name="Migration Wizard" />

      <Box sx={{p: 3, maxWidth: 1600, mx: 'auto'}}>
        {/* Section 1: Discover Related Organizations */}
        <Paper sx={{p: 3, mb: 3}}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: orgSectionExpanded ? 2 : 0,
            }}
          >
            <Typography variant="h6" sx={{fontWeight: 600}}>
              1. Discover Related Organizations
            </Typography>
            {checkSuccessful && (
              <IconButton
                size="small"
                onClick={() => setOrgSectionExpanded(!orgSectionExpanded)}
              >
                {orgSectionExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            )}
          </Box>

          <Collapse in={orgSectionExpanded}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{mb: 2, mt: orgSectionExpanded ? 0 : 2}}
            >
              Enter organization IDs or search by name. Click Check to discover
              related organizations (parent orgs, suborgs, and PL number
              matches).
            </Typography>

            <Box
              sx={{display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2}}
            >
              <Box sx={{flexGrow: 1}}>
                <OrgAutocomplete
                  multiple
                  size="small"
                  value={selectedOrgs}
                  onChange={handleOrgSelection}
                  disabled={isLoadingOrgFromUrl}
                  loading={isLoadingOrgFromUrl}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      minHeight: '42px',
                    },
                  }}
                />
              </Box>
              <Button
                variant="contained"
                onClick={handleCheck}
                disabled={
                  selectedOrgIds.length === 0 ||
                  checkOrgsMutation.isLoading ||
                  isLoadingOrgFromUrl
                }
                color="primary"
                sx={{
                  minWidth: 120,
                  height: '42px',
                }}
                startIcon={
                  (checkOrgsMutation.isLoading || isLoadingOrgFromUrl) && (
                    <CircularProgress size={16} />
                  )
                }
              >
                {checkOrgsMutation.isLoading
                  ? 'Checking...'
                  : isLoadingOrgFromUrl
                    ? 'Loading...'
                    : 'Check'}
              </Button>
            </Box>

            {/* Discovered Organizations Success Message */}
            {discoveredOrgsCount > 0 && (
              <Alert severity="success" sx={{mb: 2}}>
                Found and added {discoveredOrgsCount} related organization
                {discoveredOrgsCount > 1 ? 's' : ''} (including parent orgs and
                suborgs) to your selection.
              </Alert>
            )}

            {/* Unrelated Organizations Warning */}
            {showOrgWarning && unrelatedOrgs.length > 0 && (
              <Alert severity="warning" sx={{mb: 2}}>
                <Typography variant="body2" sx={{mb: 1}}>
                  The following organizations are unrelated and should be
                  removed:
                </Typography>
                <Box sx={{mb: 2}}>
                  {unrelatedOrgs.map(org => (
                    <Chip
                      key={org.id}
                      label={`${org.id} - ${org.name}`}
                      color="default"
                      variant="outlined"
                      size="small"
                      sx={{mr: 1, mb: 1}}
                    />
                  ))}
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAcceptAdjustedList}
                  disabled={checkOrgsMutation.isLoading}
                >
                  Remove Unrelated Organizations
                </Button>
              </Alert>
            )}
          </Collapse>

          {/* Collapsed summary */}
          {!orgSectionExpanded && checkSuccessful && (
            <Typography variant="body2" color="text.secondary">
              {selectedOrgIds.length} organization
              {selectedOrgIds.length > 1 ? 's' : ''} selected:{' '}
              {selectedOrgIds.join(', ')}
            </Typography>
          )}
        </Paper>

        {/* Section 2: Generate Migration Plan */}
        {checkSuccessful && (
          <Paper sx={{p: 3, mb: 2}}>
            <Box>
              {/* Title and Focus Dropdown Row */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{fontWeight: 600}}>
                  2. Generate Migration Plan
                </Typography>
                <FormControl size="small" sx={{minWidth: 250}}>
                  <InputLabel id="focus-select-label">Focus</InputLabel>
                  <Select
                    labelId="focus-select-label"
                    multiple
                    value={selectedFocus}
                    onChange={event => setSelectedFocus(event.target.value)}
                    input={<OutlinedInput label="Focus" />}
                    renderValue={selected => (
                      <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                        {selected.map(value => {
                          const option = focusOptions.find(
                            opt => opt.value === value
                          );
                          return (
                            <Chip
                              key={value}
                              label={option?.name || value}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {focusOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        <Checkbox
                          checked={selectedFocus.indexOf(option.value) > -1}
                        />
                        <ListItemText primary={option.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Buttons Row */}
              <Box sx={{display: 'flex', gap: 2, justifyContent: 'flex-end'}}>
                <Button
                  variant={
                    selectedPlanType === 'generate' ? 'contained' : 'outlined'
                  }
                  onClick={() => {
                    setSelectedPlanType('generate');
                    handlePreviewMigration('base');
                  }}
                  disabled={!checkSuccessful || isMigrationLoading}
                  color="primary"
                  startIcon={
                    appliedOrgIds.length > 0 &&
                    isMigrationLoading &&
                    !checkOrgsMutation.isLoading &&
                    currentPlanType === 'base' && <CircularProgress size={16} />
                  }
                >
                  Generate Base Plan
                </Button>
                <Button
                  variant={
                    selectedPlanType === 'inferred' ? 'contained' : 'outlined'
                  }
                  onClick={() => {
                    if (availableInferredPlans?.results?.length > 0) {
                      handleSelectPlanType('inferred');
                    } else {
                      setSelectedPlanType('inferred');
                      handlePreviewMigration('inferred');
                    }
                  }}
                  disabled={!checkSuccessful || isMigrationLoading}
                  color="primary"
                  startIcon={
                    appliedOrgIds.length > 0 &&
                    (isMigrationLoading || isInferredPlansLoading) &&
                    !checkOrgsMutation.isLoading &&
                    (currentPlanType === 'inferred' ||
                      selectedPlanType === 'inferred') && (
                      <CircularProgress size={16} />
                    )
                  }
                >
                  {availableInferredPlans?.results?.length > 0
                    ? 'Use'
                    : 'Generate'}{' '}
                  Inferred Plan
                </Button>
                <Button
                  variant={
                    selectedPlanType === 'user' ? 'contained' : 'outlined'
                  }
                  onClick={() => handleSelectPlanType('user')}
                  disabled={
                    !checkSuccessful ||
                    isMigrationLoading ||
                    !availableUserPlans?.results?.length
                  }
                  color="primary"
                  startIcon={
                    appliedOrgIds.length > 0 &&
                    isUserPlansLoading &&
                    !checkOrgsMutation.isLoading && (
                      <CircularProgress size={16} />
                    )
                  }
                >
                  Use Custom Plan ({availableUserPlans?.results?.length || 0})
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Section 3: Customize Plan */}
        {(activeMigrationData || importedMigrationData) &&
          !isMigrationLoading && (
            <Paper sx={{p: 3, mb: 2}}>
              <Typography variant="h6" sx={{fontWeight: 600, mb: 2}}>
                3. Customize Plan
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(_e, newMode) => {
                      if (newMode !== null) setViewMode(newMode);
                    }}
                    size="small"
                  >
                    <ToggleButton value="diagram">Diagram</ToggleButton>
                    <ToggleButton value="text">Text</ToggleButton>
                    <ToggleButton value="json">JSON</ToggleButton>
                    <ToggleButton value="validation">
                      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        Validation
                        {validationCounts.errors > 0 ? (
                          <Badge
                            badgeContent={validationCounts.errors}
                            color="error"
                            sx={{
                              '& .MuiBadge-badge': {
                                position: 'relative',
                                transform: 'none',
                                fontSize: '0.625rem',
                                minWidth: '16px',
                                height: '16px',
                              },
                            }}
                          >
                            <Box sx={{width: 0, height: 0}} />
                          </Badge>
                        ) : validationCounts.warnings > 0 ? (
                          <Badge
                            badgeContent={validationCounts.warnings}
                            color="warning"
                            sx={{
                              '& .MuiBadge-badge': {
                                position: 'relative',
                                transform: 'none',
                                fontSize: '0.625rem',
                                minWidth: '16px',
                                height: '16px',
                              },
                            }}
                          >
                            <Box sx={{width: 0, height: 0}} />
                          </Badge>
                        ) : (
                          <Check color="success" sx={{fontSize: 16}} />
                        )}
                      </Box>
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <IconButton
                    size="small"
                    onClick={e => setOperationsMenuAnchor(e.currentTarget)}
                    disabled={!checkSuccessful || isMigrationLoading}
                  >
                    <MoreHoriz />
                  </IconButton>
                  <Menu
                    anchorEl={operationsMenuAnchor}
                    open={Boolean(operationsMenuAnchor)}
                    onClose={() => setOperationsMenuAnchor(null)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        handleImportJSON();
                        setOperationsMenuAnchor(null);
                      }}
                    >
                      <Upload sx={{mr: 1}} /> Upload JSON
                    </MenuItem>
                    {(activeMigrationData || importedMigrationData) &&
                      !isMigrationLoading &&
                      viewMode === 'diagram' && (
                        <MenuItem
                          onClick={() => {
                            handleExportSVG();
                            setOperationsMenuAnchor(null);
                          }}
                        >
                          <Download sx={{mr: 1}} /> Download SVG
                        </MenuItem>
                      )}
                    {(activeMigrationData || importedMigrationData) &&
                      !isMigrationLoading &&
                      viewMode === 'diagram' && (
                        <MenuItem
                          onClick={() => {
                            handleExportPNG();
                            setOperationsMenuAnchor(null);
                          }}
                        >
                          <Image sx={{mr: 1}} /> Download PNG
                        </MenuItem>
                      )}
                    {(activeMigrationData || importedMigrationData) &&
                      !isMigrationLoading && (
                        <MenuItem
                          onClick={() => {
                            handleExportJSON();
                            setOperationsMenuAnchor(null);
                          }}
                        >
                          <FileDownload sx={{mr: 1}} /> Export JSON
                        </MenuItem>
                      )}
                  </Menu>
                  {showSaveButton && (
                    <Button
                      variant="contained"
                      onClick={handleSaveMigrationPlan}
                      size="small"
                      startIcon={<Save />}
                      color="success"
                    >
                      Save
                    </Button>
                  )}
                  {hasModifications && (
                    <>
                      <Button
                        variant="contained"
                        onClick={savePlanModifications}
                        size="small"
                        startIcon={<Save />}
                        color="primary"
                        disabled={modifyPlanMutation.isLoading}
                      >
                        {modifyPlanMutation.isLoading
                          ? 'Saving...'
                          : 'Save Changes'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={resetPlanModifications}
                        size="small"
                        startIcon={<Refresh />}
                        disabled={modifyPlanMutation.isLoading}
                      >
                        Reset
                      </Button>
                    </>
                  )}
                </Box>
                <Button
                  variant="contained"
                  onClick={handleExecutePlan}
                  color="error"
                  startIcon={
                    executeMigrationMutation.isLoading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <PlayArrow />
                    )
                  }
                  disabled={executeMigrationMutation.isLoading}
                >
                  Execute Plan
                </Button>
              </Box>
            </Paper>
          )}

        {/* Validation Warnings */}
        {appliedOrgIds.length > 0 &&
          (activeMigrationData || importedMigrationData) &&
          !isMigrationLoading && (
            <ValidationWarnings
              validation={
                (importedMigrationData || activeMigrationData)?.results
                  ?.validation
              }
            />
          )}

        {/* Preview Display */}
        {appliedOrgIds.length > 0 && (
          <Paper sx={{p: 0, overflow: 'hidden'}}>
            {isMigrationLoading && (
              <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
                <CircularProgress />
              </Box>
            )}

            {migrationError && (
              <Alert severity="error" sx={{m: 3}}>
                Error loading migration data: {migrationError.message}
              </Alert>
            )}

            {(activeMigrationData || importedMigrationData) &&
              !isMigrationLoading && (
                <>
                  {viewMode === 'diagram' ? (
                    <MigrationTreeView
                      migrationData={
                        importedMigrationData || activeMigrationData
                      }
                      organizationIds={appliedOrgIds.map(id =>
                        parseInt(id, 10)
                      )}
                      currentPlanId={currentPlanId}
                      onPlanModified={handlePlanModified}
                      onAddLinkModification={addLinkModification}
                      onRemoveLinkModification={removeUsageLinkModification}
                      onAddProductAccessModification={
                        addProductAccessModification
                      }
                      hasModifications={hasModifications}
                    />
                  ) : viewMode === 'text' ? (
                    <MigrationTextView
                      migrationData={
                        importedMigrationData || activeMigrationData
                      }
                    />
                  ) : viewMode === 'validation' ? (
                    <Box sx={{p: 3}}>
                      {isValidationLoading && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            py: 4,
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      )}

                      {validationError && (
                        <Alert severity="error" sx={{mb: 2}}>
                          Error loading validation data:{' '}
                          {validationError.message}
                        </Alert>
                      )}

                      {!isValidationLoading &&
                        !validationError &&
                        validationData && (
                          <>
                            <ValidationWarnings
                              validation={validationData?.results?.validation}
                            />
                            {!(
                              validationData?.results?.validation?.has_errors ||
                              validationData?.results?.validation?.has_warnings
                            ) && (
                              <Alert severity="success" sx={{mt: 2}}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <Check />
                                  <Typography>
                                    Migration plan validation passed
                                    successfully. No issues detected.
                                  </Typography>
                                </Box>
                              </Alert>
                            )}
                          </>
                        )}

                      {!isValidationLoading &&
                        !validationError &&
                        !validationData && (
                          <Alert severity="info">
                            <Typography>
                              No validation data available. Please select
                              organizations to validate their migration plan.
                            </Typography>
                          </Alert>
                        )}
                    </Box>
                  ) : (
                    <MigrationJsonEditor
                      migrationData={
                        importedMigrationData || activeMigrationData
                      }
                      onSave={handleJsonSave}
                      onDiscard={handleJsonDiscard}
                      isLoading={saveMigrationPlanMutation.isPending}
                    />
                  )}
                </>
              )}
          </Paper>
        )}
      </Box>

      {/* Execute Migration Confirmation Dialog */}
      <Dialog
        open={showExecuteDialog}
        onClose={() => setShowExecuteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Execute Migration Plan</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to execute this migration plan? This will
            create contracts, line items, and migration records.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowExecuteDialog(false)}
            disabled={executeMigrationMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmExecution}
            variant="contained"
            color="error"
            disabled={executeMigrationMutation.isLoading}
            startIcon={
              executeMigrationMutation.isLoading && (
                <CircularProgress size={16} />
              )
            }
          >
            {executeMigrationMutation.isLoading ? 'Executing...' : 'Execute'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MigrationWizard;
